import { useCallback, useContext, useRef, useState } from 'react';

import { DialogError } from '../../../business-error';
import { useDialog } from '../../../dialog/useDialog';
import { ToastNotificationContext } from '../../../toast-notification';
import { handleResponseError } from '../../../../../api/handleResponseError';

import { getRetryReportBusinessError } from './bussinessErrors';
import { RetriedReport, HandleRetryReportRequest } from './interfaces';

interface Props {
  handleSuccess: () => void;
  handleRetryReportRequest: HandleRetryReportRequest;
}

export const useRetryReport = ({ handleSuccess, handleRetryReportRequest }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const [retriedReport, setRetriedReport] = useState<RetriedReport>();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [isReportRetried, setLoading] = useState<boolean>(false);
  const [retryReportError, setError] = useState<DialogError>(null);

  const handleRetryReport = useCallback(async () => {
    try {
      if (retriedReport) {
        setLoading(true);
        setError(null);

        const response = await handleRetryReportRequest(retriedReport.id);

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        openToast({
          message: 'Report successfully accepted for repair',
          severity: 'success'
        });

        setLoading(false);
        handleCloseDialog();
        handleSuccess();
      }
    } catch (e) {
      const { message, severity } = getRetryReportBusinessError(e);

      setError({ message, severity });
      setLoading(false);
    }
  }, [handleCloseDialog, handleRetryReportRequest, handleSuccess, openToast, retriedReport]);

  const handleOpenRetryDialog = useRef((report: RetriedReport) => {
    handleOpenDialog();
    setRetriedReport(report);
  });

  const handleCloseRetryDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    handleOpenRetryDialog: handleOpenRetryDialog.current,
    openRetryDialog: isDialogOpen,
    handleCloseRetryDialog,
    handleRetryReport,
    isReportRetried,
    retriedReport,
    retryReportError
  };
};
